import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import SectionHeader from "@/components/SectionHeader";
import {
  Sparkles,
  Plus,
  Play,
  Pencil,
  Trash2,
  ChevronRight,
  BarChart3,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";

/**
 * Admin landing for scenarios.
 * Lists all existing scenarios, lets the admin create a new one, open the
 * editor, or run the player.
 */
export default function AdminScenarios() {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [list, lb] = await Promise.all([
        api.get("/scenarios"),
        api.get("/admin/scenarios/leaderboard"),
      ]);
      setScenarios(Array.isArray(list.data) ? list.data : []);
      setLeaderboard(lb.data?.rows || []);
    } catch (_) {
      // no-op
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (sid, title) => {
    const msg = "Delete scenario \"" + title + "\"? This also removes all attempts.";
    if (!window.confirm(msg)) return;
    try {
      await api.delete("/scenarios/" + sid);
      toast.success("Scenario deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error deleting");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-scenarios-page">
      <SectionHeader
        eyebrow="Sales scenarios"
        title="Decision-tree micro-learning"
        accent="#F4B81F"
        divider={false}
        actions={
          <Button
            onClick={() => navigate("/admin/scenarios/new")}
            className="bg-[#1B2E7A] hover:bg-[#13205A] text-white"
            data-testid="new-scenario-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New scenario
          </Button>
        }
      />

      <p className="text-sm text-[#374151] max-w-2xl">
        Build short branching scenarios where salespeople practice handling real situations — objections,
        first contact, closing, financing — by picking what they would actually say. Each pick gives
        instant feedback and points.
      </p>

      {/* Performance leaderboard */}
      {!loading && leaderboard.length > 0 && leaderboard.some((r) => r.attempts > 0) && (
        <div className="bg-white border border-[#D1D5DB] rounded-xl overflow-hidden" data-testid="scenarios-leaderboard">
          <div className="flex items-center justify-between gap-2 px-5 py-3 border-b border-[#D1D5DB] bg-[#F3F4F6]">
            <div className="inline-flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-[#1B2E7A]" strokeWidth={2} />
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#1B2E7A] font-bold">Performance leaderboard</p>
            </div>
            <p className="text-[10px] text-[#6B7280]">Hardest first · click row for details</p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white text-[#6B7280] border-b border-[#D1D5DB]">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-xs">Scenario</th>
                <th className="text-left px-4 py-2 font-medium text-xs">Linked course</th>
                <th className="text-right px-4 py-2 font-medium text-xs">Attempts</th>
                <th className="text-right px-4 py-2 font-medium text-xs">Users</th>
                <th className="text-right px-4 py-2 font-medium text-xs">Avg score</th>
                <th className="text-right px-4 py-2 font-medium text-xs">Pass rate</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((r, idx) => {
                const empty = r.attempts === 0;
                const veryHard = !empty && r.pass_rate < 40;
                const easy = !empty && r.pass_rate >= 90;
                const dotColor = empty ? "#E5E7EB" : veryHard ? "#DC2626" : easy ? "#16A34A" : "#F4B81F";
                return (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/admin/scenarios/${r.id}/analytics`)}
                    className={`cursor-pointer hover:bg-[#F9FAFB] transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-[#FAFAFB]"}`}
                    data-testid={`leaderboard-row-${r.id}`}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />
                        <span className="text-[#1A1A1A] font-medium">{r.title}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-[#6B7280] text-xs">
                      {r.course_title || <span className="italic text-[#9CA3AF]">Standalone</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#1A1A1A]">{r.attempts}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#1A1A1A]">{r.unique_users}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#1A1A1A]">{empty ? "—" : r.avg_score}</td>
                    <td className="px-4 py-2.5 text-right">
                      {empty ? (
                        <span className="text-[#9CA3AF] text-xs">No data</span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 font-semibold"
                          style={{ color: veryHard ? "#991B1B" : easy ? "#15803D" : "#854D0E" }}
                        >
                          {veryHard ? <TrendingDown className="h-3 w-3" /> : easy ? <TrendingUp className="h-3 w-3" /> : <Users className="h-3 w-3" />}
                          {r.pass_rate}%
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {loading ? (
        <div className="text-[#6B7280]">Loading...</div>
      ) : scenarios.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#D1D5DB] rounded-md">
          <Sparkles className="h-10 w-10 text-[#F4B81F] mx-auto mb-3" strokeWidth={1.4} />
          <p className="text-[#374151] mb-4">No scenarios yet. Create the first one to get started.</p>
          <Button
            onClick={() => navigate("/admin/scenarios/new")}
            className="bg-[#1B2E7A] hover:bg-[#13205A] text-white"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Create scenario
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {scenarios.map((s) => (
            <div
              key={s.id}
              className="bg-white border border-[#D1D5DB] rounded-lg p-5 flex flex-col"
              data-testid={`scenario-card-${s.id}`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="h-9 w-9 rounded-md bg-[#FEF3C7] flex items-center justify-center shrink-0">
                  <Sparkles className="h-4 w-4 text-[#854D0E]" strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-base text-[#1A1A1A] leading-snug">{s.title}</p>
                  <p className="text-xs text-[#6B7280] mt-1 line-clamp-2">{s.description || "—"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.16em] text-[#6B7280] mb-4">
                <span>{(s.nodes || []).length} nodes</span>
                <span className="text-[#D1D5DB]">·</span>
                <span>{s.pass_points || 0} pts on pass</span>
              </div>
              <div className="flex items-center gap-2 mt-auto pt-3 border-t border-[#E5E7EB]">
                <Link
                  to={`/scenarios/${s.id}`}
                  className="text-xs inline-flex items-center gap-1 text-[#1B2E7A] hover:underline"
                  data-testid={`scenario-play-${s.id}`}
                >
                  <Play className="h-3 w-3" /> Test
                </Link>
                <Link
                  to={`/admin/scenarios/${s.id}`}
                  className="text-xs inline-flex items-center gap-1 text-[#374151] hover:text-[#1B2E7A]"
                  data-testid={`scenario-edit-${s.id}`}
                >
                  <Pencil className="h-3 w-3" /> Edit
                </Link>
                <Link
                  to={`/admin/scenarios/${s.id}/analytics`}
                  className="text-xs inline-flex items-center gap-1 text-[#854D0E] hover:text-[#1A1A1A]"
                  data-testid={`scenario-analytics-${s.id}`}
                >
                  <BarChart3 className="h-3 w-3" /> Analytics
                </Link>
                <button
                  type="button"
                  onClick={() => handleDelete(s.id, s.title)}
                  className="text-xs inline-flex items-center gap-1 text-[#DC2626] hover:text-[#991B1B] ml-auto"
                  data-testid={`scenario-delete-${s.id}`}
                >
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
